import { useMutation, useQuery } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';

const { get } = erpApi();

const BASE_URL = `${urlBackend}api/v1/gestion-tickets/dashboard/`;

// ============================================================================
// Tipos del snapshot que devuelve el backend.
// ============================================================================

export interface IDashboardKpis {
  abiertos: number;
  en_proceso: number;
  cerrados_hoy: number;
  /** null si SLA esta desactivado globalmente desde el catalogo. */
  sla_vencidos: number | null;
  total: number;
}

export interface IDashboardBreakdown {
  id: string;
  label: string;
  color: string;
  count: number;
}

export interface IDashboardAsesor {
  usuario_id: number;
  usuario_nombre: string;
  count: number;
}

export interface IDashboardAsesorEstadoCount {
  id: string;
  label: string;
  color: string;
  count: number;
}

export interface IDashboardAsesorPorEstado {
  usuario_id: number;
  usuario_nombre: string;
  total: number;
  por_estado: IDashboardAsesorEstadoCount[];
}

export interface IDashboardSnapshot {
  kpis: IDashboardKpis;
  sla_habilitado: boolean;
  por_estado: IDashboardBreakdown[];
  por_prioridad: IDashboardBreakdown[];
  por_area: IDashboardBreakdown[];
  por_tipo: IDashboardBreakdown[];
  top_asesores: IDashboardAsesor[];
  asesores_por_estado: IDashboardAsesorPorEstado[];
  fecha_snapshot: string;
}

// ============================================================================
// Filtros aplicables al dashboard. El backend acepta multi-valor para
// usuario_asignado_id (varios asesores). El resto son single-value.
// ============================================================================

export interface IDashboardFiltros {
  area_destino?: string;
  estado_ticket?: string;
  prioridad?: string;
  tipo?: string;
  usuario_asignado_id?: number[];
  fecha_desde?: string;
  fecha_hasta?: string;
  /** Si true, excluye tickets cerrados (default true para vista operativa). */
  solo_abiertos?: boolean;
}

// Helper: convierte filtros a queryString. Para arrays multi-valor usa
// repeticion del param (ej. ?usuario_asignado_id=12&usuario_asignado_id=15).
function filtrosToQs(f: IDashboardFiltros & { page?: number; page_size?: number }): string {
  const qs = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      v.forEach((item) => qs.append(k, String(item)));
    } else {
      qs.set(k, String(v));
    }
  });
  return qs.toString();
}

// ============================================================================
// Hook 1: snapshot con polling configurable.
//
// refetchInterval (ms):
//   15000 / 30000 / 60000 son los valores recomendados.
//   false / 0 / null = pausado (el supervisor lo apaga manualmente).
//
// React Query ya pausa el refetch cuando la pestaña pierde foco
// (refetchIntervalInBackground=false, default). Eso evita golpear el
// backend si el supervisor se va a almorzar con el tab abierto.
// ============================================================================

export const useFetchDashboardSnapshot = (
  filtros: IDashboardFiltros,
  refetchInterval: number | false = 15000,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['dashboard-snapshot', filtros],
    queryFn: async () => {
      const qs = filtrosToQs(filtros);
      return get<IDashboardSnapshot>(`${BASE_URL}snapshot${qs ? `?${qs}` : ''}`, true);
    },
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
};

// ============================================================================
// Hook 2: tabla paginada de tickets activos. Refresh tambien por polling
// pero con interval mayor (la tabla cambia menos seguido y trae mas data).
// ============================================================================

export interface IDashboardTicketRow {
  id_ticket: string;
  codigo_ticket: string;
  cliente_razon_social: string | null;
  cliente_identificacion: string | null;
  descripcion: string | null;
  estado_ticket?: { id_ticket_estado: string; nombre: string; color: string };
  prioridad?: { id_ticket_prioridad: string; nombre: string; color: string };
  area_destino?: { id_area: string; nombre: string };
  usuario_asignado_id: number | null;
  usuario_asignado_nombre: string | null;
  fecha_creacion: string;
  fecha_cierre: string | null;
  fecha_limite_sla: string | null;
}

export const useFetchDashboardTicketsActivos = (
  filtros: IDashboardFiltros,
  page: number,
  pageSize: number,
  refetchInterval: number | false = 30000,
  enabled = true,
) => {
  return useQuery({
    queryKey: ['dashboard-tickets-activos', filtros, page, pageSize],
    queryFn: async () => {
      const qs = filtrosToQs({ ...filtros, page, page_size: pageSize });
      return get<IDashboardTicketRow[]>(`${BASE_URL}tickets-activos?${qs}`, true);
    },
    enabled,
    refetchInterval,
    refetchIntervalInBackground: false,
  });
};

// ============================================================================
// Hook 3: descargar reporte XLSX. No es polling — es un disparo manual.
// El backend devuelve binario; usamos axios crudo para manejar blobs.
// ============================================================================

export const useDescargarReporteDashboard = () => {
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: async (filtros: IDashboardFiltros) => {
      const qs = filtrosToQs(filtros);
      // Necesitamos axios directo para responseType=blob; erpApi unwrappea
      // a JSON automaticamente y rompe los binarios.
      const axios = (await import('axios')).default;
      const token = (await import('@/store/auth/auth.store')).useAuthStore.getState().token;
      const url = `${BASE_URL}reporte-xlsx${qs ? `?${qs}` : ''}`;
      const resp = await axios.get(url, {
        responseType: 'blob',
        headers: token ? { Authorization: `Token ${token}` } : {},
      });
      return resp;
    },
    onSuccess: (resp) => {
      // Descargar el blob via link sintetico.
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const filename = parseFilenameFromContentDisposition(
        resp.headers['content-disposition'] || resp.headers['Content-Disposition'],
      ) || `dashboard-tickets-${Date.now()}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const capped = resp.headers['x-report-capped'] === 'true';
      const rows = resp.headers['x-report-rows'];
      const msg = capped
        ? `Reporte descargado pero limitado a ${rows} filas (cap de seguridad). Aplica mas filtros para reducir el dataset.`
        : `Reporte descargado (${rows} tickets).`;
      setAlert?.(true, msg, capped ? AlertSeverityEnum.warning : AlertSeverityEnum.success);
    },
    onError: (err) => handleAxiosError(err),
  });
};

function parseFilenameFromContentDisposition(cd?: string): string | null {
  if (!cd) return null;
  const match = /filename\*?=(?:UTF-\d+''|")?([^";]+)"?/i.exec(cd);
  return match ? decodeURIComponent(match[1]).replace(/^"|"$/g, '') : null;
}
